import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";

const SUPPORT_WHATSAPP_NUMBER = "919080432374";
const SUPPORT_MESSAGE =
  "Hi, I have registered for Senior Penetration Tester demo. Please guide me with next steps.";

const nextSteps = [
  {
    icon: "fa-solid fa-envelope-circle-check",
    title: "Verify Your Email",
    description:
      "Please check your inbox and verify your email address to complete your Senior Penetration Tester registration confirmation.",
  },
  {
    icon: "fa-solid fa-phone-volume",
    title: "Expect a Callback",
    description:
      "If you selected callback, our counselling team will contact you with batch timing, course structure, and fee details.",
  },
  {
    icon: "fa-solid fa-calendar-check",
    title: "Check Batch Updates",
    description:
      "You will receive upcoming batch details, demo updates, learning platform access information, and enrollment guidance.",
  },
  {
    icon: "fa-solid fa-user-shield",
    title: "Prepare for Advanced VAPT",
    description:
      "Get ready to learn advanced web security, API security, Android testing, thick client testing, reporting, and real-world assessments.",
  },
];

const courseHighlights = [
  "Advanced AppSec and VAPT training",
  "API penetration testing methodology",
  "Android application security testing",
  "Thick client penetration testing",
  "Real-world vulnerability reporting",
  "Career-focused interview preparation",
  "Hands-on tools and practical labs",
  "Advanced security assessment workflow",
];

function ThankYouSenior() {
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = "Thank You Senior Registration | Practical Infosec";

    confetti({
      particleCount: 180,
      spread: 80,
      origin: { y: 0.6 },
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "senior_demo_registration_success",
      course: "SENIOR PENETRATION TESTER",
      page: "Thank You Senior",
    });

    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead");
    }

    function handleScroll() {
      setShowBackToTop(window.scrollY > 300);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function openWhatsAppChat() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }

  function openWhatsAppAppFallback() {
    const encoded = encodeURIComponent(SUPPORT_MESSAGE);
    const deepLink = `whatsapp://send?phone=${SUPPORT_WHATSAPP_NUMBER}&text=${encoded}`;
    const webLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encoded}`;

    window.location.href = deepLink;

    setTimeout(() => {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }, 700);
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950">
      {/* HERO */}
      <section className="relative px-4 py-20 overflow-hidden md:px-20 md:py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(220,38,38,0.35),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(124,58,237,0.30),transparent_36%),radial-gradient(circle_at_center,rgba(37,99,235,0.18),transparent_46%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 to-transparent"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
            {/* LEFT CONTENT */}
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-rose-300 bg-white/10 border-white/15 backdrop-blur">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Registration Successful
              </span>

              <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight text-white md:text-6xl">
                Thanks for Registering{" "}
                <span className="inline-block">🎉</span>
              </h1>

              <p className="max-w-3xl mt-6 text-lg font-semibold leading-8 text-slate-300">
                Your registration for the{" "}
                <span className="font-black text-rose-300">
                  Senior Penetration Tester
                </span>{" "}
                course has been successfully submitted.
              </p>

              <div className="p-5 mt-8 border rounded-3xl bg-white/10 border-white/15 backdrop-blur">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center flex-shrink-0 text-2xl text-green-300 border w-14 h-14 rounded-2xl bg-green-400/10 border-green-400/20">
                    <i className="fa-solid fa-envelope-circle-check"></i>
                  </div>

                  <div>
                    <h2 className="text-xl font-black text-white">
                      Please Verify Your Email
                    </h2>

                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
                      We’ve sent a confirmation email to your inbox. Please
                      verify your email and keep checking your inbox or WhatsApp
                      for upcoming demo and batch updates.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 mt-8 sm:flex-row">
                <button
                  type="button"
                  onClick={openWhatsAppChat}
                  className="inline-flex items-center justify-center py-4 text-base font-black text-white transition shadow-xl px-7 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-green-700/30 hover:scale-105"
                >
                  <i className="mr-2 fa-brands fa-whatsapp"></i>
                  Chat on WhatsApp
                </button>

                <Link
                  to="/senior-penetration-tester"
                  className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
                >
                  View Course Details
                </Link>
              </div>
            </div>

            {/* RIGHT SUCCESS CARD */}
            <div className="relative">
              <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-r from-red-500/30 via-purple-500/30 to-blue-500/30 blur-2xl"></div>

              <div className="relative overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur">
                <div className="p-8 text-center bg-white">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto text-5xl text-green-600 bg-green-100 rounded-full shadow-inner">
                    <i className="fa-solid fa-check"></i>
                  </div>

                  <h2 className="mt-6 text-3xl font-black text-slate-950">
                    Submission Received
                  </h2>

                  <p className="mt-3 text-base font-semibold leading-7 text-slate-600">
                    Our team will review your registration and contact you with
                    the next steps for the Senior Penetration Tester program.
                  </p>

                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                      <p className="text-xl font-black text-purple-700">
                        Senior
                      </p>
                      <p className="mt-1 text-xs font-bold text-slate-600">
                        Penetration Tester
                      </p>
                    </div>

                    <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
                      <p className="text-xl font-black text-green-700">
                        Success
                      </p>
                      <p className="mt-1 text-xs font-bold text-slate-600">
                        Registration Status
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t bg-slate-950/95 border-white/10">
                  <p className="text-xs font-black tracking-widest uppercase text-rose-300">
                    Need Help?
                  </p>

                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
                    Contact our support team anytime for course, batch, fee, or
                    learning platform guidance.
                  </p>

                  <div className="flex flex-col gap-3 mt-5 sm:flex-row">
                    <a
                      href="tel:+91-9080432374"
                      className="inline-flex items-center justify-center flex-1 px-4 py-3 text-sm font-black text-white transition bg-purple-700 rounded-xl hover:bg-purple-600"
                    >
                      <i className="mr-2 fa-solid fa-phone"></i>
                      Call
                    </a>

                    <a
                      href="mailto:support@practicalinfosec.com"
                      className="inline-flex items-center justify-center flex-1 px-4 py-3 text-sm font-black transition bg-white rounded-xl text-slate-950 hover:bg-rose-100"
                    >
                      <i className="mr-2 fa-solid fa-envelope"></i>
                      Email
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="relative px-4 py-20 overflow-hidden bg-white md:px-20">
        <div className="absolute top-0 right-0 rounded-full w-96 h-96 bg-purple-200/30 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 rounded-full w-96 h-96 bg-red-200/20 blur-3xl"></div>

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-purple-700 uppercase bg-purple-100 border border-purple-200 rounded-full">
              What Happens Next?
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              Your Advanced Cybersecurity Journey Starts Here
            </h2>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-600">
              Follow the next steps below to make sure you do not miss important
              updates from Practical Infosec.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 mt-12 md:grid-cols-2 xl:grid-cols-4">
            {nextSteps.map((step, index) => (
              <div
                key={step.title}
                className="p-6 transition bg-white border shadow-lg group rounded-3xl border-slate-200 hover:-translate-y-2 hover:border-purple-300 hover:shadow-2xl"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-center w-16 h-16 text-2xl text-white transition rounded-2xl bg-gradient-to-br from-red-600 via-purple-700 to-blue-700 group-hover:scale-110">
                    <i className={step.icon}></i>
                  </div>

                  <span className="text-4xl font-black text-slate-100">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="mt-6 text-xl font-black text-slate-950">
                  {step.title}
                </h3>

                <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSE HIGHLIGHTS */}
      <section className="px-4 py-20 bg-slate-50 md:px-20">
        <div className="grid items-center gap-10 mx-auto max-w-7xl lg:grid-cols-[0.9fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest text-purple-700 uppercase bg-purple-100 border border-purple-200 rounded-full">
              Program Highlights
            </span>

            <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-5xl">
              Why Senior Penetration Tester?
            </h2>

            <p className="mt-5 text-lg font-semibold leading-8 text-slate-600">
              This program is designed for learners who want to move beyond
              basics and build strong practical capability in advanced VAPT,
              API security, Android security, thick client testing, and
              professional reporting.
            </p>

            <div className="flex flex-col gap-4 mt-8 sm:flex-row">
              <Link
                to="/senior-penetration-tester"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-red-600 via-purple-700 to-blue-700 hover:scale-105"
              >
                Explore Full Curriculum
              </Link>

              <Link
                to="/blog"
                className="inline-flex items-center justify-center py-4 text-base font-black transition bg-white border px-7 rounded-2xl text-slate-950 border-slate-200 hover:bg-slate-950 hover:text-white"
              >
                Read Cybersecurity Blogs
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {courseHighlights.map((highlight) => (
              <div
                key={highlight}
                className="flex items-start gap-3 p-5 bg-white border shadow-sm rounded-2xl border-slate-200"
              >
                <span className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm text-white bg-purple-700 rounded-xl">
                  <i className="fa-solid fa-check"></i>
                </span>

                <p className="text-base font-black leading-6 text-slate-800">
                  {highlight}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 py-20 bg-white md:px-20">
        <div className="relative max-w-6xl p-8 mx-auto overflow-hidden shadow-2xl rounded-[2rem] bg-gradient-to-r from-slate-950 via-purple-950 to-slate-950 md:p-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.25),transparent_35%)]"></div>

          <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-black tracking-widest uppercase text-rose-300">
                Stay Connected
              </p>

              <h2 className="mt-3 text-3xl font-black text-white md:text-5xl">
                Have questions before joining the advanced batch?
              </h2>

              <p className="max-w-3xl mt-4 text-base font-semibold leading-7 text-slate-300">
                Our team can help you understand batch timing, fee structure,
                advanced curriculum, lab access, reporting practice, and career
                support.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <button
                type="button"
                onClick={openWhatsAppChat}
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition px-7 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 hover:scale-105"
              >
                WhatsApp Support
              </button>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center py-4 text-base font-black text-white transition border px-7 rounded-2xl border-white/20 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHATSAPP STICKY BUTTON */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setWhatsappOpen((prev) => !prev);
        }}
        className="fixed z-50 p-3 px-4 text-white transition -translate-y-1/2 rounded-full shadow-lg cursor-pointer top-1/2 right-6 animate-blink hover:opacity-90 bg-gradient-to-r from-green-400 via-green-500 to-green-600"
        aria-label="Join WhatsApp"
      >
        <i className="text-4xl fa-brands fa-whatsapp"></i>
      </button>

      {/* WHATSAPP MODAL */}
      {whatsappOpen && (
        <div
          className="fixed z-50 p-4 transition-opacity duration-300 -translate-y-1/2 bg-white shadow-xl top-1/2 right-20 rounded-xl w-72 max-sm:right-4 max-sm:left-4 max-sm:w-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsappTitle"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setWhatsappOpen(false);
            }}
            className="absolute text-3xl text-gray-600 cursor-pointer top-2 left-4 hover:text-red-500"
            aria-label="Close"
          >
            &times;
          </button>

          <h2
            id="whatsappTitle"
            className="mt-6 mb-2 text-lg font-bold text-center text-gray-800"
          >
            Unsure about which course to choose?
          </h2>

          <p className="mb-3 text-sm text-center text-gray-700">
            Tap below to start a WhatsApp chat — we&apos;re here to help!
          </p>

          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={openWhatsAppChat}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-white transition bg-green-600 rounded-full hover:bg-green-700"
              aria-label="Chat Now on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp"></i>
              Chat Now
            </button>

            <button
              type="button"
              onClick={openWhatsAppAppFallback}
              className="inline-flex items-center justify-center w-full gap-2 px-3 py-2 text-sm text-gray-800 transition border border-gray-200 rounded-full hover:bg-gray-50"
              aria-label="Open in WhatsApp App"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.52 3.48A11.93 11.93 0 0012 .5C5.73.5.99 5.24.99 11.5c0 2.01.53 3.95 1.53 5.68L0 24l6.97-1.79c1.61.45 3.28.68 4.9.68 6.27 0 11.01-4.74 11.01-11 0-3.02-1.18-5.85-3.36-7.41zM12 21.5c-1.44 0-2.86-.34-4.14-.99l-.3-.15-4.14 1.06 1.15-3.98-.18-.31A8.12 8.12 0 013 11.5c0-4.97 4.06-9 9-9 2.41 0 4.66.94 6.36 2.64a8.95 8.95 0 012.64 6.36c0 4.97-4.06 9-9 9z" />
              </svg>
              Open in App
            </button>
          </div>
        </div>
      )}

      {/* BACK TO TOP BUTTON */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed z-50 p-4 text-white transition bg-purple-700 rounded-lg shadow-lg cursor-pointer bottom-12 right-7 hover:bg-purple-800"
          aria-label="Back to top"
        >
          <i className="text-xl fa-solid fa-arrow-up"></i>
        </button>
      )}

      {/* FLOATING CALL BUTTON */}
      <a
        href="tel:+91-9080432374"
        className="fixed z-50 p-3 text-white transition rounded-md shadow-lg cursor-pointer bg-violet-600 bottom-60 right-7 hover:bg-green-700"
        aria-label="Call Us"
      >
        <i className="text-2xl fa-solid fa-phone"></i>
      </a>
    </main>
  );
}

export default ThankYouSenior;